import { TFunction } from 'i18next';

export interface IResumableMedia {
  shortUUID?: any;
  feedTitle: string;
  feedDesc: string;
  remoteLocation?: string;
  mediaId?: string;
  notificationChannelId?: string;
  deleteMedia?: any;
  mediaFile?: IImageOrVideoFile;
  totalFileSize?: number;
  totalBytesUploaded?: number;
  fileName: string;
  name: string;
  status: FeedUploadStatusEnum;
  feedIndex?: number;
  channelId?: string | number;
  videoId: string | null | number;
  videoPeerTubeUrl: string;
  offsetFrom?: number;
  duration?: number;
  previewUrl: string;
  translation?: TFunction<'translation', undefined>;
  videoRawUrl?: string;
  isPublished?: boolean;
  isTransCoded?: boolean;
  importStatus?: boolean;
}

export interface IImageOrVideoFile {
  /**
   * URI to the local image or video file (usable as the source of an `Image` element, in the case of
   * an image) and `width` and `height` specify the dimensions of the media.
   */
  uri: string;
  /**
   * The unique ID that represents the picked image or video, if picked from the library. It can be used
   * by [expo-media-library](./media-library) to manage the picked asset.
   *
   * > This might be `null` when the ID is unavailable or the user gave limited permission to access the media library.
   * > On Android, the ID is unavailable when the user selects a photo by directly browsing file system.
   *
   * @platform ios
   * @platform android
   */
  assetId?: string | null;
  /**
   * Width of the image or video.
   */
  width: number;
  /**
   * Height of the image or video.
   */
  height: number;
  /**
   * The type of the asset.
   */
  type?: 'image' | 'video';
  /**
   * Preferred filename to use when saving this item. This might be `null` when the name is unavailable
   * or user gave limited permission to access the media library.
   *
   * @platform ios
   */
  fileName?: string | null;
  /**
   * File size of the picked image or video, in bytes.
   *
   * @platform ios
   */
  fileSize?: number;
  /**
   * The `exif` field is included if the `exif` option is truthy, and is an object containing the
   * image's EXIF data. The names of this object's properties are EXIF tags and the values are the
   * respective EXIF values for those tags.
   */
  exif?: Record<string, any> | null;
  /**
   * When the `base64` option is truthy, it is a Base64-encoded string of the selected image's JPEG data, otherwise `null`.
   * If you prepend this with `'data:image/jpeg;base64,'` to create a data URI,
   * you can use it as the source of an `Image` element; for example:
   * ```ts
   * <Image
   *   source={{ uri: 'data:image/jpeg;base64,' + asset.base64 }}
   *   style={{ width: 200, height: 200 }}
   * />
   * ```
   */
  base64?: string | null;
  /**
   * Length of the video in milliseconds or `null` if the asset is not a video.
   */
  duration?: number | null;
  mimeType?: string | null;
  file?: File;
  previewUrl?: string;
}

export enum FeedUploadStatusEnum {
  PENDING,
  INPROGRESS,
  COMPLETE,
  ERROR,
  PUBLISHED,
}

export interface IMediaFile {
  /**
   * Document original name.
   */
  name: string;
  /**
   * Document size in bytes.
   */
  size?: number;
  /**
   * An URI to the local document file.
   */
  uri: string;
  /**
   * Document MIME type.
   */
  mimeType?: string;
  /**
   * Timestamp of last document modification.
   */
  lastModified?: number;
  /**
   * `File` object for the parity with web File API.
   * @platform web
   */
  file?: File;
}

export type DocumentOrMediaFile = IMediaFile | IImageOrVideoFile;

export interface ICreateResumableMedia {
  channelId: string | number;
  filename: string;
  name: string;
  category?: string;
  commentsEnabled?: boolean;
  description?: string;
  downloadEnabled?: boolean;
  language?: string;
  nsfw?: boolean;
  thumbnailfile?: Buffer;
  waitTranscoding?: boolean;
}

// function getIntervals(totalSize: number, segments: number) {
//   const c = Math.floor(totalSize / segments);
//   const r: Array<Record<number, number>> = [];
//   for (var i = 0; i <= totalSize; i += c) {
//     const a = i == 0 ? i : (i += 1);
//     const b = i + c > totalSize ? totalSize : i + c;
//     if (a < totalSize) r.push([a, b]);
//   }
//   return r;
// }
